const DEBUG_CODE_PARAM = 'debug_code';
const DEBUG_SESSION_KEY = 'xh-debug-session';
const DEBUG_PUBLIC_KEY: JsonWebKey = {
    key_ops: ['verify'],
    ext: true,
    kty: 'EC',
    x: '9DEvVmDobuUuMfgVw4Xyj4suiawWPdCbwJMBz66D9FE',
    y: 'lnK4KQd3_mYrxY9eJS2epTh1_Blm2jvJTqBT4O0tHIM',
    crv: 'P-256'
};

interface DebugSession {
    debugCode: string;
}

// 管理调试链接参数和当前标签页的限时调试状态。
export async function resolveDebugAccess(): Promise<boolean> {
    try {
        const url = new URL(window.location.href);
        const debugCode = url.searchParams.get(DEBUG_CODE_PARAM);

        if (debugCode) {
            removeDebugCodeFromUrl(url);

            const expiresAt = await verifyDebugCode(debugCode);
            if (expiresAt) {
                saveDebugSession(debugCode);
                return true;
            }
        }

        return await hasActiveDebugSession();
    } catch (error) {
        // 调试能力失败不能阻塞支付页自身启动。
        console.warn('调试码校验失败:', error);
        return false;
    }
}

// 调试码不应继续留在地址栏、浏览器历史或用户截图中。
function removeDebugCodeFromUrl(url: URL): void {
    url.searchParams.delete(DEBUG_CODE_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

// 调试码格式为“过期时间.签名”，生产端只使用公钥验签。
async function verifyDebugCode(debugCode: string): Promise<number | null> {
    const [expiresAtText, receivedSignature, ...rest] = debugCode.split('.');
    if (!expiresAtText || !receivedSignature || rest.length > 0) {
        return null;
    }

    const expiresAt = Number.parseInt(expiresAtText, 36);
    if (!Number.isSafeInteger(expiresAt) || expiresAt <= Date.now()) {
        return null;
    }

    const isValid = await verifyDebugSignature(expiresAtText, receivedSignature);
    return isValid ? expiresAt : null;
}

async function verifyDebugSignature(expiresAtText: string, signatureText: string): Promise<boolean> {
    if (!window.crypto?.subtle) {
        return false;
    }

    const encoder = new TextEncoder();
    const key = await window.crypto.subtle.importKey(
        'jwk',
        DEBUG_PUBLIC_KEY,
        { name: 'ECDSA', namedCurve: 'P-256' },
        false,
        ['verify']
    );
    const signature = decodeBase64Url(signatureText);
    if (!signature) {
        return false;
    }

    return window.crypto.subtle.verify(
        { name: 'ECDSA', hash: 'SHA-256' },
        key,
        signature,
        encoder.encode(expiresAtText)
    );
}

function decodeBase64Url(value: string): Uint8Array | null {
    try {
        const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
        return Uint8Array.from(window.atob(padded), char => char.charCodeAt(0));
    } catch (error) {
        return null;
    }
}

function saveDebugSession(debugCode: string): void {
    try {
        const session: DebugSession = { debugCode };
        sessionStorage.setItem(DEBUG_SESSION_KEY, JSON.stringify(session));
    } catch (error) {
        console.warn('无法保存调试会话状态:', error);
    }
}

async function hasActiveDebugSession(): Promise<boolean> {
    try {
        const sessionText = sessionStorage.getItem(DEBUG_SESSION_KEY);
        if (!sessionText) {
            return false;
        }

        const session = JSON.parse(sessionText) as Partial<DebugSession>;
        if (typeof session.debugCode === 'string' && await verifyDebugCode(session.debugCode)) {
            return true;
        }

        sessionStorage.removeItem(DEBUG_SESSION_KEY);
    } catch (error) {
        console.warn('无法读取调试会话状态:', error);
    }

    return false;
}
